import type { AssetClassId } from '../types';
import type { AssetClass, BusinessArchetype } from './schema';

/**
 * Six asset classes spanning the risk spectrum. The numbers are game
 * abstractions tuned for a ~50-year horizon: `growth` beats `conservative` on
 * average but loses badly in a bust, and `speculative` has a fat left tail that
 * ruins undiversified players often enough to matter.
 */
const ASSETS: AssetClass[] = [
  {
    id: 'cash',
    label: 'Cash',
    blurb: 'Instantly available. Quietly erodes.',
    expectedReturn: 0.005,
    volatility: 0.004,
    liquidity: 1,
    beta: 0,
    minInvestment: 0,
  },
  {
    id: 'conservative',
    label: 'Conservative',
    blurb: 'Bonds and deposits. Low return, low drama, always sellable.',
    expectedReturn: 0.031,
    volatility: 0.045,
    liquidity: 0.97,
    beta: 0.25,
    minInvestment: 500,
  },
  {
    id: 'balanced',
    label: 'Balanced',
    blurb: 'A broad mix. Boring on purpose, and hard to beat over decades.',
    expectedReturn: 0.058,
    volatility: 0.108,
    liquidity: 0.93,
    beta: 0.7,
    minInvestment: 1000,
  },
  {
    id: 'growth',
    label: 'Growth',
    blurb: 'Equity-heavy. Rewards patience; punishes anyone who needs the money.',
    expectedReturn: 0.079,
    volatility: 0.185,
    liquidity: 0.9,
    beta: 1.15,
    minInvestment: 1500,
  },
  {
    id: 'speculative',
    label: 'Speculative',
    blurb: 'Concentrated bets. Occasionally life-changing, frequently not.',
    expectedReturn: 0.115,
    volatility: 0.46,
    liquidity: 0.72,
    beta: 1.8,
    minInvestment: 2000,
    shock: { chance: 0.11, min: -0.72, max: -0.3 },
  },
  {
    id: 'property',
    label: 'Property',
    blurb: 'Leveraged, illiquid, and doubles as somewhere to live.',
    expectedReturn: 0.042,
    volatility: 0.095,
    liquidity: 0.35,
    beta: 0.6,
    minInvestment: 20000,
  },
];

export const ASSET_CLASSES: Record<AssetClassId, AssetClass> = Object.fromEntries(
  ASSETS.map((a) => [a.id, a]),
) as Record<AssetClassId, AssetClass>;

export const ASSET_CLASS_LIST: readonly AssetClass[] = ASSETS;

/** Market regimes modulate every risk asset through its beta. */
export const MARKET_REGIMES = {
  boom: { drift: 0.07, volMultiplier: 0.8, meanYears: 5 },
  normal: { drift: 0, volMultiplier: 1, meanYears: 7 },
  soft: { drift: -0.035, volMultiplier: 1.25, meanYears: 3 },
  bust: { drift: -0.14, volMultiplier: 1.8, meanYears: 2 },
} as const;

/**
 * Eight business archetypes. They differ on the axes that actually create
 * different lives: capital intensity, time demand, and how survivable failure is.
 */
const BUSINESSES: BusinessArchetype[] = [
  {
    id: 'biz.freelance',
    label: 'Freelance practice',
    blurb: 'You are the product. Low capital, low risk, hard ceiling.',
    startingCapital: 3000,
    initialRevenueMultiple: 9,
    costRatio: 0.24,
    growthMean: 0.07,
    growthVolatility: 0.16,
    risk: 24,
    timeDemand: 58,
    exitMultiple: 0.8,
    keySkills: ['creative', 'communication'],
    tags: ['low-capital', 'safe', 'low-ceiling'],
  },
  {
    id: 'biz.services',
    label: 'Services agency',
    blurb: 'Hire people, sell their hours. Scales with management, not genius.',
    startingCapital: 22000,
    initialRevenueMultiple: 3.4,
    costRatio: 0.7,
    growthMean: 0.14,
    growthVolatility: 0.22,
    risk: 42,
    timeDemand: 74,
    exitMultiple: 2.6,
    keySkills: ['management', 'communication', 'business'],
    requires: [{ k: 'skill', skill: 'business', op: '>=', value: 30 }],
    tags: ['people-heavy', 'moderate'],
  },
  {
    id: 'biz.retail',
    label: 'Local retail',
    blurb: 'A lease, a stock room, and a queue. Steady until the street changes.',
    startingCapital: 48000,
    initialRevenueMultiple: 2.6,
    costRatio: 0.82,
    growthMean: 0.06,
    growthVolatility: 0.19,
    risk: 54,
    timeDemand: 80,
    exitMultiple: 2.1,
    keySkills: ['business', 'social'],
    tags: ['capital-heavy', 'grinding'],
  },
  {
    id: 'biz.food',
    label: 'Food business',
    blurb: 'Beloved, brutal margins, and the highest failure rate on the board.',
    startingCapital: 65000,
    initialRevenueMultiple: 2.9,
    costRatio: 0.86,
    growthMean: 0.1,
    growthVolatility: 0.3,
    risk: 68,
    timeDemand: 88,
    exitMultiple: 1.8,
    keySkills: ['business', 'creative', 'management'],
    tags: ['high-risk', 'exhausting', 'passion'],
  },
  {
    id: 'biz.saas',
    label: 'Software product',
    blurb: 'Near-zero marginal cost. Years of nothing, then possibly everything.',
    startingCapital: 30000,
    initialRevenueMultiple: 1.1,
    costRatio: 0.44,
    growthMean: 0.36,
    growthVolatility: 0.55,
    risk: 72,
    timeDemand: 82,
    exitMultiple: 5.5,
    keySkills: ['technical', 'business'],
    requires: [{ k: 'skill', skill: 'technical', op: '>=', value: 45 }],
    tags: ['high-risk', 'high-ceiling', 'slow-start'],
  },
  {
    id: 'biz.trade',
    label: 'Trade contracting',
    blurb: 'Tools, a van, and a reputation. Boringly reliable if you show up.',
    startingCapital: 26000,
    initialRevenueMultiple: 3.8,
    costRatio: 0.66,
    growthMean: 0.09,
    growthVolatility: 0.15,
    risk: 34,
    timeDemand: 78,
    exitMultiple: 1.6,
    keySkills: ['technical', 'business'],
    requires: [{ k: 'skill', skill: 'technical', op: '>=', value: 40 }],
    tags: ['reliable', 'physical'],
  },
  {
    id: 'biz.consulting',
    label: 'Consulting firm',
    blurb: 'Sells your track record at a premium. Needs a real reputation first.',
    startingCapital: 14000,
    initialRevenueMultiple: 5.5,
    costRatio: 0.48,
    growthMean: 0.16,
    growthVolatility: 0.24,
    risk: 38,
    timeDemand: 70,
    exitMultiple: 2.4,
    keySkills: ['business', 'communication', 'management'],
    requires: [
      { k: 'num', path: 'careerReputation', op: '>=', value: 55 },
      { k: 'num', path: 'yearsExperience', op: '>=', value: 8 },
    ],
    tags: ['reputation-gated', 'late-game'],
  },
  {
    id: 'biz.creative',
    label: 'Creative studio',
    blurb: 'Makes things you are proud of. Cash flow is a permanent negotiation.',
    startingCapital: 12000,
    initialRevenueMultiple: 3.2,
    costRatio: 0.6,
    growthMean: 0.11,
    growthVolatility: 0.34,
    risk: 56,
    timeDemand: 68,
    exitMultiple: 1.9,
    keySkills: ['creative', 'communication'],
    requires: [{ k: 'skill', skill: 'creative', op: '>=', value: 45 }],
    tags: ['meaning', 'volatile'],
  },
];

export const BUSINESS_ARCHETYPES: Record<string, BusinessArchetype> = Object.fromEntries(
  BUSINESSES.map((b) => [b.id, b]),
);

export const BUSINESS_ARCHETYPE_LIST: readonly BusinessArchetype[] = BUSINESSES;
